import React from 'react';
import * as S from './toggle.styles';

interface IToggleBaseProps {
    className?: string;
    disabled?: boolean;
    value: boolean;
    onChange: (value: boolean) => void;
}

/**
 * A switch must say WHAT it switches: a screen reader announces an unnamed one as just "switch, on", which tells the
 * listener nothing. So the type demands exactly one way of naming it, and leaving both out does not compile.
 */
type IToggleName =
    | {
          /** The name as text ("Stream", "Account issuance"), for a switch with no visible label to point at. */
          'aria-label': string;
          'aria-labelledby'?: undefined;
      }
    | {
          /** The id of the visible element that already names it, so the spoken name cannot drift from the screen. */
          'aria-labelledby': string;
          'aria-label'?: undefined;
      };

export type IToggleProps = IToggleBaseProps & IToggleName;

/**
 * An on/off switch. Rendered as a native button with `role="switch"` and `aria-checked`, so it is reachable with the
 * Tab key, flips with Space or Enter, and is announced with its state. It must be named, with `aria-label` or
 * `aria-labelledby` (see `IToggleProps`).
 */
export const Toggle: React.FunctionComponent<IToggleProps> = (props: IToggleProps): React.ReactElement => {
    const { className, disabled, value, onChange } = props;

    const handleClick = (): void => {
        if (!disabled) {
            onChange && onChange(!value);
        }
    };

    return (
        <S.Toggle
            type="button"
            role="switch"
            aria-checked={value}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            className={className}
            disabled={!!disabled}
            on={value}
            onClick={handleClick}
        >
            <S.Knob />
        </S.Toggle>
    );
};
